// Interactive location map for the Hotel Details page.
// - Renders one hotel pin (with a name InfoWindow) on Google Maps.
// - Optionally surfaces nearby transit/restaurant POIs via the Places
//   library (already loaded by `useGoogleMaps`). Any failure falls back
//   to just the hotel pin so the section always renders when we have
//   coordinates.
// - Google-Directions link opens in a new tab.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { MapPin, Navigation, Bus, Utensils, Landmark } from 'lucide-react';
import { useGoogleMaps } from '../../lib/googleMaps';

const containerStyle = { width: '100%', height: '360px', borderRadius: '12px' };

const POI_TYPES = [
  { type: 'transit_station', label: 'Transit', icon: Bus, color: '#2563eb' },
  { type: 'restaurant',      label: 'Dining',  icon: Utensils, color: '#dc2626' },
  { type: 'tourist_attraction', label: 'Attractions', icon: Landmark, color: '#059669' },
];

const HotelDetailMap = ({ hotel }) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [pois, setPois] = useState([]);           // [{name, position, type}]
  const [activeMarker, setActiveMarker] = useState('hotel');
  const [enabledPoi, setEnabledPoi] = useState({ transit_station: true, restaurant: false, tourist_attraction: false });
  const mapRef = useRef(null);

  const center = useMemo(() => {
    const c = hotel?.coordinates;
    if (c?.latitude && c?.longitude) return { lat: Number(c.latitude), lng: Number(c.longitude) };
    return null;
  }, [hotel?.coordinates]);

  // Fetch nearby POIs whenever the enabled set changes. Uses Places JS SDK
  // (already loaded) so no extra key work required. Silently swallows quota
  // errors — the pin still renders.
  useEffect(() => {
    if (!isLoaded || !center || !mapRef.current) return;
    let cancelled = false;
    const service = new window.google.maps.places.PlacesService(mapRef.current);
    const collected = [];
    const activeTypes = Object.keys(enabledPoi).filter((t) => enabledPoi[t]);
    if (activeTypes.length === 0) { setPois([]); return; }
    let pending = activeTypes.length;
    activeTypes.forEach((type) => {
      service.nearbySearch(
        { location: center, radius: 1200, type },
        (results, status) => {
          if (!cancelled && status === window.google.maps.places.PlacesServiceStatus.OK && Array.isArray(results)) {
            results.slice(0, 6).forEach((r) => {
              collected.push({
                id: r.place_id,
                name: r.name,
                position: { lat: r.geometry.location.lat(), lng: r.geometry.location.lng() },
                type,
              });
            });
          }
          pending -= 1;
          if (pending === 0 && !cancelled) setPois(collected);
        },
      );
    });
    return () => { cancelled = true; };
  }, [isLoaded, center, enabledPoi]);

  if (!center) return null; // No coords → nothing to show.

  if (loadError) {
    return (
      <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm p-4">
        Map couldn&apos;t load. <a className="underline" target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${center.lat},${center.lng}`}>Open in Google Maps</a>
      </div>
    );
  }

  if (!isLoaded) {
    return <div className="h-[360px] rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />;
  }

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${center.lat},${center.lng}`;

  return (
    <div className="space-y-3" data-testid="hotel-detail-map">
      <div className="relative">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={15}
          onLoad={(m) => { mapRef.current = m; }}
          options={{
            disableDefaultUI: false, streetViewControl: false, mapTypeControl: false,
            fullscreenControl: true, zoomControl: true, clickableIcons: false,
            gestureHandling: 'cooperative',
          }}
        >
          <Marker
            position={center}
            title={hotel.name}
            onClick={() => setActiveMarker('hotel')}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12, fillColor: '#dc2626', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3,
            }}
          />
          {activeMarker === 'hotel' && (
            <InfoWindow position={center} onCloseClick={() => setActiveMarker(null)}>
              <div className="text-xs max-w-[220px]">
                <p className="font-bold text-gray-900 mb-0.5">{hotel.name}</p>
                <p className="text-gray-600">{[hotel.address, hotel.city].filter(Boolean).join(', ')}</p>
                <a href={directionsUrl} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-red-600 font-semibold">
                  <Navigation className="h-3 w-3" /> Directions
                </a>
              </div>
            </InfoWindow>
          )}
          {pois.map((p) => {
            const meta = POI_TYPES.find((m) => m.type === p.type);
            return (
              <Marker
                key={p.id}
                position={p.position}
                title={p.name}
                onClick={() => setActiveMarker(p.id)}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 6, fillColor: meta?.color || '#111', fillOpacity: 0.9, strokeColor: '#fff', strokeWeight: 2,
                }}
              >
                {activeMarker === p.id && (
                  <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                    <div className="text-xs">
                      <p className="font-bold text-gray-900">{p.name}</p>
                      <p className="text-gray-500">{meta?.label}</p>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            );
          })}
        </GoogleMap>
      </div>

      {/* POI toggles + directions */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Nearby places">
          {POI_TYPES.map(({ type, label, icon: Icon, color }) => {
            const active = enabledPoi[type];
            return (
              <button
                key={type}
                type="button"
                onClick={() => setEnabledPoi((s) => ({ ...s, [type]: !s[type] }))}
                data-testid={`map-poi-toggle-${type}`}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium transition ${
                  active
                    ? 'text-white border-transparent shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-red-300'
                }`}
                style={active ? { backgroundColor: color } : {}}
              >
                <Icon className="h-3 w-3" /> {label}
              </button>
            );
          })}
        </div>
        <a
          href={directionsUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="hotel-detail-directions"
          className="inline-flex items-center gap-1 text-sm text-red-600 hover:underline font-semibold"
        >
          <Navigation className="h-4 w-4" /> Directions
        </a>
      </div>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
        <MapPin className="h-3 w-3" /> {center.lat.toFixed(5)}, {center.lng.toFixed(5)}
      </p>
    </div>
  );
};

export default HotelDetailMap;
